import { Link } from 'react-router';
import { COMPANY } from '@/content/company';
import { Clause, LegalPage } from './LegalPage';
import legal from './LegalPage.module.css';

/**
 * Payment, Refund & Cancellation Policy.
 *
 * Exists as its own page rather than as a clause of the Terms for a practical
 * reason: payment providers and enterprise procurement scan the footer for a
 * link that says "refund", and the Terms open by disclaiming that they govern
 * delivered work. A refund policy nested inside a document that says it is not
 * about the commercial relationship is a refund policy nobody can rely on.
 *
 * The clause order deliberately tracks the questions a payment provider's
 * review asks, in the order it asks them: what is sold, in what currency, how
 * it is billed, when it is delivered, how it is cancelled, how money comes
 * back, how card data is handled, and who is out of scope.
 */

const TOC = [
  '1. Scope',
  '2. Pricing & currency',
  '3. Invoicing & payment',
  '4. How work is delivered',
  '5. Changes to scope',
  '6. Cancellation',
  '7. Refunds & corrections',
  '8. Returns',
  '9. Payment security',
  '10. Who we can work with',
  '11. Billing questions',
] as const;

const mail = <a href={`mailto:${COMPANY.email}`}>{COMPANY.email}</a>;

export function PaymentRefunds() {
  return (
    <LegalPage
      eyebrow="Payment & refunds"
      title="Payment, Refund & Cancellation Policy"
      toc={TOC}
      intro={
        <>
          This policy explains how {COMPANY.legalName} prices, invoices and delivers work, and what
          happens if an engagement is cancelled or something we have billed turns out to be wrong.
          It applies alongside the individual contract or order confirmation for your project.
        </>
      }
    >
      <Clause n={1} title="What this policy covers">
        <p>
          We supply professional services: software design, development, integration and technical
          consulting. We do not sell physical goods, downloadable products, subscriptions or licence
          keys through this website, and no payment is taken on skabro.eu itself.
        </p>
        <p>
          We contract with businesses and organisations, not with consumers. Where a signed contract
          or order confirmation says something different from this page, the signed document
          prevails.
        </p>
      </Clause>

      <Clause n={2} title="Pricing and currency">
        <p className={legal.runOn}>
          Work is quoted individually and invoiced in <strong>euro (EUR)</strong>.
        </p>
        <p>
          We do not publish list prices, because scope determines cost. You receive a written
          quotation stating the fee, the currency and the payment schedule before any work begins,
          and nothing is chargeable until you accept it in writing. A quotation is valid for 30 days
          unless it says otherwise.
        </p>
        <p>
          Prices are exclusive of VAT. VAT is applied under Slovak and EU rules: Slovak VAT for
          domestic clients, and the reverse charge for VAT-registered businesses in other EU member
          states, who account for VAT in their own country. Our VAT number is {COMPANY.icDph}.
        </p>
      </Clause>

      <Clause n={3} title="Invoicing and payment terms">
        <p>
          Unless the signed agreement states otherwise, invoices are payable within 14 days of
          issue. Fixed-scope projects are invoiced against agreed milestones; ongoing work is
          invoiced monthly in arrears.
        </p>
        <p>
          Payment is by SEPA bank transfer against a standard invoice, or by card or direct debit
          through a regulated third-party payment provider where we have agreed that with you in
          advance. Bank charges levied by your own bank are yours; we do not add surcharges for any
          payment method.
        </p>
        <p>
          Late payment carries statutory interest under Slovak law. We would always rather talk than
          charge it - if an invoice is going to be late, tell us before the due date.
        </p>
      </Clause>

      <Clause n={4} title="How and when work is delivered">
        <p>
          Work begins on the date agreed in writing in the order or contract. Delivery is by
          milestone: each increment is delivered for your review as it is completed, rather than in
          a single handover at the end. You can see and use what you have paid for as the project
          runs.
        </p>
        <p>
          Where an engagement is ongoing rather than fixed-scope, work is delivered continuously and
          invoiced monthly in arrears, so you are never paying in advance for time not yet spent.
        </p>
        <p>
          Delivery is electronic - source code, deployed environments, documentation and access
          credentials, transferred by the means agreed with you. There is no physical shipment and
          no delivery charge.
        </p>
      </Clause>

      <Clause n={5} title="Changes to scope">
        <p>
          If you ask for something outside the agreed scope, we tell you what it costs and what it
          affects before we build it. Additional work is only chargeable once you have approved it
          in writing. We do not invoice for scope you did not agree to.
        </p>
      </Clause>

      <Clause n={6} title="Cancellation">
        <p>
          Either party may end an ongoing engagement by giving 30 days&rsquo; written notice. Work
          performed up to the end of the notice period remains payable, and we will hand over
          everything completed to that point.
        </p>
        <p>
          A fixed-scope project may be cancelled at any time in writing. You pay for work completed
          and for work already commissioned from third parties on your behalf; you do not pay for
          the remainder of the scope. If you have paid in advance and the work has not been done,
          the unearned balance is refunded under clause 7.
        </p>
        <p>
          Either party may end an engagement immediately if the other commits a material breach and
          does not remedy it within 14 days of being asked to in writing.
        </p>
      </Clause>

      <Clause n={7} title="Refunds and corrections">
        <p className={legal.continues}>
          Because we deliver a bespoke service made to your order, work that has been performed and
          accepted is not refundable. That is the general rule, and these are the exceptions to it:
        </p>
        <ul>
          <li>
            <strong>You paid in advance and we have not done the work.</strong> The unearned balance
            is refunded in full, by the method you paid, within 14 days of the engagement ending.
          </li>
          <li>
            <strong>We invoiced you incorrectly.</strong> Tell us within 14 days of the invoice date
            and we will correct it or issue a credit note.
          </li>
          <li>
            <strong>A delivered increment does not do what we agreed it would.</strong> Tell us
            within 14 days of delivery. We will fix it at our own cost. If we cannot fix it within a
            reasonable time, we credit or refund the amount attributable to that increment.
          </li>
        </ul>
        <p>
          Refunds are made to the original payment method. We do not charge a fee for processing a
          refund.
        </p>
        <p>
          No consumer right of withdrawal applies, because we contract with businesses rather than
          with consumers - see clause 1 and our <Link to="/terms">Terms of Use</Link>.
        </p>
      </Clause>

      <Clause n={8} title="Returns">
        <p>
          We supply services, not physical goods, so there is nothing to ship back and no returns
          process. Anything that is wrong with delivered work is handled as a correction or a credit
          note under clause 7.
        </p>
      </Clause>

      <Clause n={9} title="Payment security">
        <p>
          No payment is taken on this website. skabro.eu is an informational site: it has no
          checkout, no payment form and no shopping basket, and it never asks you for card or bank
          details.
        </p>
        <p>
          Where a card or direct debit payment is agreed, it is processed by a regulated third-party
          payment provider on its own systems. Card details are entered with that provider directly.{' '}
          {COMPANY.legalName} does not see, transmit, process or store card numbers or bank
          credentials at any point, and we hold no cardholder data to secure.
        </p>
        <p>
          This site is served over HTTPS only, with HSTS and a strict Content-Security-Policy. It
          sets no cookies of its own, and the only third party it loads is the Google reCAPTCHA
          protecting the contact page - never on a payment or policy page. How we handle the
          personal data you do send us is set out in our <Link to="/privacy">Privacy Policy</Link>,
          and the reCAPTCHA in our <Link to="/cookies">Cookie Policy</Link>.
        </p>
      </Clause>

      <Clause n={10} title="Who we can work with">
        <p>
          We work with businesses and organisations in the European Union and, case by case, beyond
          it. We do not enter engagements with parties subject to European Union or United Nations
          sanctions, and we do not supply work where doing so would breach EU export control rules
          or the law of the country you operate in.
        </p>
        <p>
          We may decline or discontinue an engagement where these restrictions apply, or where we
          cannot complete the client due diligence our own obligations require. Where we decline
          before work begins, nothing is chargeable.
        </p>
      </Clause>

      <Clause n={11} title="Billing questions and disputes">
        <p>
          Please raise any billing question with us directly at {mail} before starting a chargeback
          or dispute with a payment provider. We aim to answer billing enquiries within one working
          day, and almost everything is resolved faster that way than through a dispute process.
        </p>
        <p>
          If we cannot resolve it between us, the governing law and jurisdiction are those set out
          in clause 10 of our <Link to="/terms">Terms of Use</Link>: the law of the Slovak Republic,
          and the competent Slovak courts.
        </p>
        <p>
          We may update this policy. The version published here at the time your quotation was
          issued is the one that applies to that engagement.
        </p>
      </Clause>
    </LegalPage>
  );
}
